import { Alert, Box, IconButton, Typography } from "@outschool/backpack";
import { centsToDollars } from "@outschool/business-rules";
import {
  EsaCreditTransactionsQuery,
  EsaCreditTransactionsQueryVariables
} from "@outschool/gql-frontend-generated";
import { faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import React, { useState } from "react";

import { useEsaMembershipCreditBalances } from "../../hooks/useEsaMembershipCreditBalance";
import { useUpdateCurrentUserMutation } from "../../queries/CurrentUser";
import { CreditTransactionListing } from "../transactions/utils";

const esaTransactionsQuery = gql`
  query EsaCreditTransactions($limit: Int!, $offset: Int!) {
    transactionListings(limit: $limit, offset: $offset) {
      totalCount
      data {
        id
        date
        type
        ... on CreditTransactionListing {
          creditPurchase {
            amount_cents
            amount_granted_credits
            learner {
              name
            }
          }
        }
      }
    }
  }
`;

export default function EsaMembershipBanner({
  bannerClosedAt
}: {
  bannerClosedAt: string | null | undefined;
}) {
  const { t } = useTranslation("client\\components\\banners\\EsaBanner");
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const { creditBalances, loading: balancesLoading } =
    useEsaMembershipCreditBalances();
  const [isClosed, setIsClosed] = useState(false);
  const { loading, data } = useQueryWithPreviousData<
    EsaCreditTransactionsQuery,
    EsaCreditTransactionsQueryVariables
  >(esaTransactionsQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      limit: 10,
      offset: 0
    }
  });

  if (isClosed || loading || balancesLoading || !data) {
    return null;
  }

  // assuming all learners have the same funding program
  const programName = creditBalances?.[0].programName;

  const transactionListings = data?.transactionListings
    ?.data as CreditTransactionListing[];
  if (!transactionListings || transactionListings.length === 0) {
    return null;
  }

  // displaying most recent
  const { creditPurchase, date } = transactionListings[0];

  if (bannerClosedAt && date < bannerClosedAt) {
    setIsClosed(true);
  }

  const handleClose = async () => {
    setIsClosed(true);
    await updateCurrentUser({
      variables: { details: { ["bannerClosedAt"]: new Date().toISOString() } }
    });
  };

  return (
    <Alert
      severity="success"
      noAction
      sx={{
        display: "flex",
        paddingY: "6px",
        justifyContent: "center",
        alignItems: "center",
        border: "none",
        borderRadius: "0"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <Typography variant="subtitle1" sx={{ color: "success" }}>
          {t(
            "{{learnerName}} received {{amountCredits}} credits (worth ${{dollarAmount}}) from {{programName}}",
            {
              learnerName: creditPurchase?.learner?.name,
              amountCredits: creditPurchase?.amount_granted_credits,
              dollarAmount: centsToDollars(
                creditPurchase?.amount_cents ?? null
              ),
              programName
            }
          )}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="medium"
          icon={faTimes}
          variant="text"
          color="success"
          sx={{ padding: "4px" }}
        />
      </Box>
    </Alert>
  );
}
